/**
 * Services Page — Route Module
 */
import { routes, type Route } from '@letsgethome/routes';
import {
  assetPath,
  type AssetPath,
  type Locale,
  type LocalizedText,
} from '@letsgethome/types/branded';
import { Button } from '@letsgethome/ui/button';
import { cn } from '@letsgethome/ui/lib/utils';
import { PageContainer } from '@letsgethome/ui/page-container';
import { Z_LAYERS } from '@letsgethome/ui/z-layers';

import { FaqDisclosureList } from '../../../../shared/components/FaqDisclosureList';
import { SeoHead } from '../../../../shared/components/SeoHead';
import Image from '../../../../shared/components/ui/Image';
import { useStaticSeo } from '../../../../shared/hooks/seo/use-static-seo';
import { Link } from '../../../../shared/i18n/routing';
import { useLocale, useTranslations } from '../../../../shared/i18n/useTranslations';
import { faqsFromCatalog } from '../../../../shared/lib/seo/faq-jsonld';

export function Component() {
  const t = useTranslations('services');
  // One value: the JSON-LD below and the disclosure list further down are the
  // same array, so the page cannot describe an answer it does not serve.
  const faqs = faqsFromCatalog(t.dyn, t.indices('faqs.list'));
  // Service illustrations are published per site locale with the BCP-47 tag as
  // the filename suffix (e.g. `…_zh-HK.webp`) — `useLocale()` already returns
  // the typed `Locale` tag, so the suffix IS the locale. No hand locale→suffix
  // map, no silent `?? 'en'` fallback: enabling a locale reaches this surface
  // by construction (its localized assets must be published alongside).
  const fileLocale: Locale = useLocale();

  return (
    <>
      <SeoHead
        {...useStaticSeo('/services', { title: t('seo.title'), description: t('seo.description') })}
        structuredData={[faqs.jsonLd]}
      />
      <PageContainer size="7xl" className="py-12 sm:px-6 lg:px-8">
        <div className="mb-16 space-y-4 text-center">
          <h1 className="text-3xl font-bold md:text-5xl">{t('title')}</h1>
          <p className="mx-auto max-w-3xl text-lg text-muted-foreground">{t('subtitle')}</p>
          <p className="text-sm text-muted-foreground">{t('scrollToExplore')}</p>
        </div>

        {/*
         * The two tiers are STACKED, not tabbed, and that is load-bearing.
         *
         * Radix's `TabsContent` renders `children: present && children`, so an
         * inactive panel serves `<div data-state="inactive" hidden></div>` with
         * NOTHING inside it — the same defect that deleted every FAQ answer on
         * this site (B.3a / #4749). Probing prod confirmed it on all three
         * locales: both `-content-self` tabpanels were empty elements, and the
         * only copy of the Self Listing tier's copy anywhere in the document was
         * the i18n message payload inside a <script>. `/services` is
         * `indexable: true`; half its service copy reached no crawler and no
         * reader who did not click.
         *
         * `forceMount` is NOT the repair. Radix computes `hidden: !present`, so
         * `forceMount` makes BOTH panels visible at once and the tabs stop being
         * tabs; the only way to keep the tab affordance is to hide the mounted
         * panel with CSS — text in the DOM that no reader can reach, which is a
         * worse thing to ship than thin content.
         *
         * A `<details>` disclosure (#4749's pattern) is right for FAQs and wrong
         * here: these two tiers are the product, not a long tail of questions,
         * and they are a COMPARISON — Assisted against Self. A UI that shows one
         * side of a comparison at a time is the wrong affordance regardless of
         * crawlers. Stacked, the page reads the way its own subtitle already
         * promises ("Scroll to explore our services"), and `ServiceCard`'s
         * left/right alternation — authored before the tabs and pointless inside
         * them, since you never saw two cards at once — finally does something.
         *
         * The tab TRIGGER labels were the only place the tier was named, so they
         * become each card's eyebrow. No string leaves the catalog.
         */}
        <section id="buy-rent-services" className="mb-16">
          <div className="mb-8 text-center">
            <p className="text-lg font-medium text-muted-foreground">{t('buyRent.subtitle')}</p>
            <h2 className="text-2xl font-bold md:text-3xl">{t('buyRent.title')}</h2>
          </div>

          <div className="space-y-16">
            <ServiceCard
              tier={t('tabs.assisted')}
              title={t('buyRent.assisted.title')}
              features={t
                .indices('buyRent.assisted.features')
                .map((i) => t.dyn(`buyRent.assisted.features.${i}`))}
              ctaText={t('buyRent.cta')}
              ctaHref={routes.listings.rent()}
              imageSrc={assetPath(`/services/buy_rent_assisted_listing_${fileLocale}.webp`)}
              imageAlt={t('buyRent.assisted.title')}
              imageSide="end"
            />

            <ServiceCard
              tier={t('tabs.self')}
              title={t('buyRent.self.title')}
              features={t
                .indices('buyRent.self.features')
                .map((i) => t.dyn(`buyRent.self.features.${i}`))}
              ctaText={t('buyRent.cta')}
              ctaHref={routes.listings.rent()}
              imageSrc={assetPath(`/services/buy_rent_self_listing_${fileLocale}.webp`)}
              imageAlt={t('buyRent.self.title')}
              imageSide="start"
            />
          </div>
        </section>

        <div className="mb-16 border-t border-border" />

        <section id="sell-lease-services" className="mb-16">
          <div className="mb-8 text-center">
            <p className="text-lg font-medium text-muted-foreground">{t('sellLease.subtitle')}</p>
            <h2 className="text-2xl font-bold md:text-3xl">{t('sellLease.title')}</h2>
          </div>

          <div className="space-y-16">
            <ServiceCard
              tier={t('tabs.assisted')}
              title={t('sellLease.assisted.title')}
              features={t
                .indices('sellLease.assisted.features')
                .map((i) => t.dyn(`sellLease.assisted.features.${i}`))}
              ctaText={t('sellLease.cta')}
              ctaHref={routes.account.listings.new({ listingType: 'assisted-listing' })}
              imageSrc={assetPath(`/services/sell_lease_assisted_listing_${fileLocale}.webp`)}
              imageAlt={t('sellLease.assisted.title')}
              imageSide="end"
            />

            <ServiceCard
              tier={t('tabs.self')}
              title={t('sellLease.self.title')}
              features={t
                .indices('sellLease.self.features')
                .map((i) => t.dyn(`sellLease.self.features.${i}`))}
              ctaText={t('sellLease.cta')}
              ctaHref={routes.account.listings.new({ listingType: 'self-listing' })}
              imageSrc={assetPath(`/services/sell_lease_self_listing_${fileLocale}.webp`)}
              imageAlt={t('sellLease.self.title')}
              imageSide="start"
            />
          </div>
        </section>

        <section className="mb-16">
          <h2 className="mb-8 text-center text-2xl font-bold md:text-3xl">{t('faqs.title')}</h2>
          <div className="mx-auto max-w-3xl">
            <FaqDisclosureList faqs={faqs} />
          </div>
        </section>

        <section className="mb-16">
          <h2 className="mb-8 text-center text-2xl font-bold md:text-3xl">
            {t('socialProof.title')}
          </h2>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {t.indices('socialProof.testimonials').map((i) => (
              <TestimonialCard
                key={i}
                text={t.dyn(`socialProof.testimonials.${i}.text`)}
                name={t.dyn(`socialProof.testimonials.${i}.name`)}
                rating={5}
              />
            ))}
          </div>
        </section>

        <section className="relative overflow-hidden rounded-3xl bg-info p-8 text-center text-info-foreground md:p-16">
          <div className="absolute -bottom-1/4 -left-1/4 size-64 rounded-full bg-white/10" />
          <div className="absolute -right-1/4 -top-1/4 size-48 rounded-full bg-white/10" />
          <h2
            className={cn(
              'relative mb-6 text-xl font-bold md:text-2xl',
              Z_LAYERS.contentOverBackdrop.className
            )}
          >
            {t('closing')}
          </h2>
          <Link
            href={routes.listings.rent()}
            prefetch="intent"
            className={cn(
              'relative inline-flex rounded-lg bg-background px-6 py-2 font-medium text-foreground hover:bg-background/90',
              Z_LAYERS.contentOverBackdrop.className
            )}
          >
            {t('learnMore')}
          </Link>
        </section>
      </PageContainer>
    </>
  );
}

/**
 * One service tier, rendered unconditionally.
 *
 * `imageSide` replaces the old `imageFirst` boolean, which said the opposite of
 * what it did: `imageFirst = true` put the TEXT column first and the image on
 * the right. Inside the tabs the alternation was invisible anyway (one card on
 * screen at a time); stacked it is the layout, so the prop now names the side
 * the image actually lands on and has no default to guess wrong.
 */
function ServiceCard({
  tier,
  title,
  features,
  ctaText,
  ctaHref,
  imageSrc,
  imageAlt,
  imageSide,
}: {
  tier: LocalizedText;
  title: LocalizedText;
  features: LocalizedText[];
  ctaText: LocalizedText;
  ctaHref: Route;
  imageSrc: AssetPath;
  imageAlt: LocalizedText;
  imageSide: 'start' | 'end';
}) {
  return (
    <div className="mx-auto max-w-4xl">
      <div className="grid grid-cols-1 items-center gap-12 md:grid-cols-12 md:gap-24">
        <div className={`md:col-span-7 ${imageSide === 'start' ? 'md:order-last' : ''}`}>
          <h3 className="mb-6">
            <span className="mb-2 block text-sm font-semibold uppercase tracking-wide text-info-text">
              {tier}
            </span>
            <span className="text-xl font-semibold">{title}</span>
          </h3>
          <ul className="mb-8 space-y-4">
            {features.map((feature, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="mt-0.5 shrink-0 text-info-text">✓</span>
                <span className="text-muted-foreground">{feature}</span>
              </li>
            ))}
          </ul>
          <div className="flex justify-center md:justify-start">
            <Button asChild size="sm" className="rounded-lg">
              <Link href={ctaHref} prefetch="intent">
                {ctaText}
              </Link>
            </Button>
          </div>
        </div>
        <div className="md:col-span-5">
          <Image
            src={imageSrc}
            alt={imageAlt}
            width={500}
            height={400}
            className="w-full"
            sizes="(max-width: 768px) 100vw, 40vw"
            unoptimized
          />
        </div>
      </div>
    </div>
  );
}

function TestimonialCard({
  text,
  name,
  rating,
}: {
  text: LocalizedText;
  name: LocalizedText;
  rating: number;
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-6">
      <div className="mb-3 flex gap-0.5">
        {Array.from({ length: rating }).map((_, i) => (
          <span key={i} className="text-highlight-text">
            ★
          </span>
        ))}
      </div>
      <p className="mb-4 text-muted-foreground">&ldquo;{text}&rdquo;</p>
      <p className="font-medium">{name}</p>
    </div>
  );
}
